<template>
  <div
    v-if="gambit"
    class="gam-gambit-card"
    :class="[getClass, { 'is-concluded': isConcluded, 'is-respond-challenge': respondBadge === 'challenge' }]"
  >
    <RouterLink v-if="isLink" :to="getGambitLinkProps" class="gambit-link" />
    <GamGambitOwner v-if="gambit.canEdit" class="owner-tag" :is-corner="isMobile" is-gambit-organizer is-list />

    <div class="title-timeframe">
      <div class="timeframe">
        <span class="date-time">
          <GamGambitDayTag v-if="isTodayOrTomorrow" :date="gambit.start" :end="gambit.end" class="date" />

          <span v-else class="date">
            {{ GambitHelper.formatTime(gambit.start, { omitTime: true }).join(' ') }}
          </span>

          &middot;

          <span class="time start-time">
            {{ GambitHelper.formatTime(gambit.start, { omitDay: true }).join(' ') }}
          </span>
        </span>
      </div>

      <GamChip v-if="showUnreadChat" class="new-message" v-bind="newMessageTag" />
    </div>

    <div v-if="location" class="location">
      <span class="name">{{ location.name }}</span>
    </div>

    <div class="gambit-tags-wrapper">
      <GamGambitSimpleTags
        :gambit="gambit"
        :size="GamChipSize.X_SMALL"
        :show-challenge="!respondBadge"
        show-attendees
        show-rating
        show-category
        show-types
        show-recurring
        is-horizontally-padded
        highlight-attendance
        show-need-players
        :respond-badge="respondBadge"
      />
    </div>
  </div>
</template>

<script lang="ts">
// export interface GambitLocation {
//   name: string;
//   address: string;
//   type: LocationType;
//   private: boolean;
//   favorite: boolean;
// }

interface GambitLocationDto {
  name: string;
  address: string;
  type: LocationType;
  private: boolean;
  favorite: boolean;
}

export type GamResultCardProps = {
  gambit: BaseGambitDto;
  location: GambitLocationDto | null;
  isLink?: boolean;
  isHighlight?: boolean;
  isWide?: boolean;
  selected?: boolean;
  showDistance?: boolean;
  respondBadge?: 'challenge' | 'invitation';
};
</script>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import type { LocationType } from '@/core/data/location/location.type';
import GambitHelper from '@/core/helpers/gambit.helper';

import { isMobile } from '@/core/helpers/ui.helper';
import GamChip from '@/views/components/GamChip.vue';
import GamGambitSimpleTags from '@/views/components/gam-gambits/GamGambitSimpleTags.vue';
import { GamChipSize } from '@/views/composables/constants/components/gamChip.constants';
import { useMinuteClock } from '@/views/composables/hooks/useMinuteClock';
import { addDays, isAfter, isSameDay } from 'date-fns';

import { computed } from 'vue';
import GamGambitDayTag from './GamGambitDayTag.vue';
import GamGambitOwner from './GamGambitOwner.vue';

const props = withDefaults(defineProps<GamResultCardProps>(), {
  showDistance: true,
  location: null,
  respondBadge: undefined,
});

const getClass = computed(() => {
  return {
    selected: props.selected,
    'is-wide': props.isWide,
    'is-list': true,
    'is-mobile': isMobile,
  };
});

const getGambitLinkProps = computed(() => {
  return {
    name: RouterNameType.GAMBIT,
    params: { id: props.gambit?.id },
    query: { back: 'history' },
  };
});

const newMessageTag = computed(() => GambitHelper.getNewMessageTag(GamChipSize.X_SMALL));

const showUnreadChat = computed(
  () => !!props.gambit.hasUnreadChatMessages && GambitHelper.isChatAccessible(props.gambit),
);

const now = useMinuteClock();

const isTodayOrTomorrow = computed(() => {
  const start = new Date(props.gambit.start);
  return isSameDay(start, now.value) || isSameDay(start, addDays(now.value, 1));
});

const isConcluded = computed(() => {
  return props.gambit?.end ? isAfter(now.value, new Date(props.gambit.end)) : false;
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-gambit-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-md);

  position: relative;
  width: 100%;

  padding-bottom: 32px;
  border: 1px solid transparent;
  padding: 16px;
  text-decoration: none;
  position: relative;

  @extend %tinted-card;

  &.selected {
    border-color: var(--color-highlight);
    @extend %active-tinted-card;
  }

  @include hover {
    background-color: var(--color-dark-500);
    box-shadow: var(--shadow-card-hover);
  }

  .gambit-link {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
  }

  .owner-tag {
    position: absolute;
    top: 0;
    right: 0;
    pointer-events: none;
  }

  .title-timeframe {
    display: flex;
    align-items: center;
    gap: 0.5em;
    width: 100%;

    .timeframe {
      width: auto;
      min-width: 0;
    }
  }

  .new-message {
    flex: none;
    pointer-events: none;
  }

  .title {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    position: relative;
    overflow: hidden;
    width: 100%;
    margin-top: -1px;
    margin-bottom: 1px;
    letter-spacing: 0.02em;
    font-weight: 700;
    // font-weight: 600;
    --webkit-font-smoothing: subpixel-antialiased;

    span {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      text-overflow: ellipsis;
      word-break: break-word;
      hyphens: auto;
      overflow: hidden;
      color: var(--color-white);
      -webkit-text-fill-color: var(--color-white);
      padding-right: 2em;
    }
  }

  &.is-respond-challenge .title span {
    background: var(--color-linear-gradient);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .timeframe {
    display: flex;
    gap: 0.3em;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: pre;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-white-60);

    span:nth-child(3) {
      text-overflow: ellipsis;
      overflow: hidden;
    }

    span:nth-child(4) {
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .date-time {
      display: flex;
      align-items: center;
      gap: 0.05em;
      color: var(--color-white-60);
    }

    .date {
      color: var(--color-white);
    }

    .time {
      color: var(--color-white-60);
    }

    .start-time {
      color: var(--color-white);
    }
  }

  &.is-wide {
    .title-timeframe {
      position: relative;
      width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5em;
    }

    .title {
      width: auto;
      flex-shrink: 0;
      margin-top: 0;
      margin-bottom: 0;
    }

    .timeframe {
      width: auto;
      flex-shrink: 0;
      transform: translateY(1px);
    }
  }

  .location {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.33rem;
    width: 100%;
    // font-size: 14px;
    font-size: 14px;
    letter-spacing: 0.02em;

    .icon {
      transform: translateY(-1px);
    }

    .icon :deep(svg) {
      color: var(--color-white-60);
    }

    .name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex-shrink: 0;
      max-width: calc(100% - 28px);
      color: var(--color-white);
    }

    .address,
    .distance {
      color: var(--color-white-25);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .distance {
      padding-left: 0.2em;
    }

    .amount {
      letter-spacing: 0.05em;
    }
  }

  &.is-concluded .location .name {
    color: var(--color-white-30);
  }

  &.is-concluded .title span {
    background: none;
    -webkit-text-fill-color: var(--color-white-30);
    color: var(--color-white-30);
  }

  &.is-concluded .timeframe {
    .date,
    .start-time,
    .date-time {
      color: var(--color-white-60);
    }

    :deep(.day-tag) {
      -webkit-background-clip: unset !important;
      background-clip: unset !important;
      -webkit-text-fill-color: var(--color-white-60) !important;
      background: none !important;
      color: var(--color-white-60) !important;
    }
  }

  .gam-gambit-date {
    z-index: 6;
    padding-left: 8px;
    top: 0;
    left: 0;
    bottom: calc(32px + 8px);
    padding-bottom: 20px;
    padding-left: 16px;
  }

  .gam-gambit-location {
    z-index: 3;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100% - 40px);
    mask-image: linear-gradient(rgb(0 0 0 / 100%), transparent);
  }

  .gambit-tags-wrapper {
    margin-top: 4px;
    margin-left: -16px;
    margin-right: -16px;
    width: calc(100% + 32px);
    position: relative;
    z-index: 3;
  }
}

.gambit-base-info {
  width: 100%;
}

@keyframes hilight {
  0% {
    color: var(--color-white);
  }

  75% {
    color: var(--color-white);
  }

  100% {
    color: var(--color-pink);
  }
}

:deep(.day-tag.now),
:deep(.day-tag.soon),
:deep(.day-tag.today),
:deep(.day-tag.tomorrow) {
  -webkit-background-clip: unset !important;
  background-clip: unset !important;
  -webkit-text-fill-color: var(--color-pink) !important;
  background: none !important;
  color: var(--color-pink) !important;
}
</style>
